import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Calendar, GraduationCap, Clock, Megaphone, Newspaper, AlertTriangle } from 'lucide-react';
import { getAnnouncementBar } from '../services/api';
import { getKindMeta } from '../utils/announcementStatus';
import Countdown from './announcements/Countdown';
import './AnnouncementBar.css';

const KIND_ICON = {
  intake: Calendar, scholarship: GraduationCap, deadline: Clock,
  event: Megaphone, news: Newspaper, alert: AlertTriangle,
};

const DAY_MS = 24 * 60 * 60 * 1000;

function dismissedKey(slug) {
  return `ve_dismissed_announcement_${slug}`;
}

/** Site-wide bar above the navbar. Renders nothing on 204/error/loading —
 * there is no loading shell, so it can never flash empty or cause layout
 * shift. Its own rendered height is published as --ve-announcement-bar-height
 * so Navbar.css's mobile-menu offset can compensate without a hardcoded
 * pixel value (see the two `calc()` uses in Navbar.css). */
export default function AnnouncementBar() {
  const { pathname } = useLocation();
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getAnnouncementBar()
      .then((data) => {
        if (cancelled) return;
        setAnnouncement(data);
        setDismissed(data ? sessionStorage.getItem(dismissedKey(data.slug)) === '1' : false);
      })
      .catch(() => { if (!cancelled) setAnnouncement(null); });
    return () => { cancelled = true; };
  }, []);

  const isAdminRoute = pathname.startsWith('/admin-portal');
  const visible = !isAdminRoute && !!announcement && !dismissed;

  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !barRef.current) {
      root.style.setProperty('--ve-announcement-bar-height', '0px');
      return undefined;
    }
    const el = barRef.current;
    const update = () => root.style.setProperty('--ve-announcement-bar-height', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--ve-announcement-bar-height', '0px');
    };
  }, [visible]);

  if (!visible) return null;

  const kindMeta = getKindMeta(announcement.kind);
  const Icon = KIND_ICON[announcement.kind] || Newspaper;
  const isExternal = /^https?:\/\//i.test(announcement.cta_url || '');
  const msToClose = announcement.closes_at ? new Date(announcement.closes_at) - Date.now() : null;
  const showCountdown = msToClose !== null && msToClose > 0 && msToClose <= 30 * DAY_MS;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissedKey(announcement.slug), '1');
    setDismissed(true);
  };

  return (
    <section aria-label="Site announcement" className={`ann-bar ve-tone-${kindMeta.tone}`} ref={barRef}>
      <div className="ann-bar-inner">
        <div className="ann-bar-status" role="status">
          <Icon size={16} className="ann-bar-icon" aria-hidden="true" />
          <p className="ann-bar-text">{announcement.summary}</p>
          {showCountdown && <Countdown closesAt={announcement.closes_at} className="ann-bar-countdown" />}
          {announcement.cta_url && (
            isExternal ? (
              <a href={announcement.cta_url} target="_blank" rel="noopener noreferrer" className="ann-bar-cta">
                {announcement.cta_label || 'Learn more'}
              </a>
            ) : (
              <Link to={announcement.cta_url} className="ann-bar-cta">
                {announcement.cta_label || 'Learn more'}
              </Link>
            )
          )}
        </div>
        <button
          type="button"
          className="ann-bar-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss this announcement"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
